/**
 * Single source of truth for the admin panel sections (spec 028).
 *
 * `NAV_GROUPS` is the drawer menu of `Home.vue`; the same list feeds the
 * "Sección de inicio" selector of the Profile, so a section can only be chosen
 * if it is in the menu. Each leaf has a stable `key`: the admin's preference
 * stores the key (backend `users-admin.preferences.defaultSection`), never the
 * route, so a route can move without breaking saved preferences. Keys must stay
 * unique lowercase slugs (the backend rejects anything else) and must never be
 * renamed once released — renaming one silently resets that preference.
 *
 * Per-admin permissions (specs 131/132): every leaf declares `permission`, a tag
 * from the backend catalog — `<key>:view`, except `register-service`
 * (any of `service-records:read` / `service-records:read-own`, spec 132
 * Phase 12) and the pure action pages `mrr-billing-entry`
 * (`mrr-billing-entry:create`), `mrr-billing-import` (`mrr-billing-import:import`)
 * and `ranger-test-chat`. `permission` may be a list of
 * alternative tags (any-of). The menu, the Profile selector
 * and the landing are all filtered with `isSectionAllowed`, and the router
 * applies the same rule through each child route's `meta.permission`. A leaf
 * without `permission` (safety net for a leaf added without one) is visible
 * only to Super admins (`*`).
 */

import { can, isPermissionDeclared, isSuperAdmin } from './permissions';

export const NAV_GROUPS = [
  {
    title: 'Métricas',
    icon: 'mdi-chart-line',
    subLinks: [
      {
        key: 'mrr-resume',
        link: '/finances/metas',
        title: 'MRR Resumen',
        icon: 'mdi-target',
        permission: 'mrr-resume:view'
      },
      {
        key: 'mrr-breakdown',
        link: '/finances/mrr-detalle',
        title: 'MRR Breakdown',
        icon: 'mdi-table-search',
        permission: 'mrr-breakdown:view'
      },
      {
        key: 'mrr-new',
        link: '/metricas/new-mrr',
        title: 'MRR New',
        icon: 'mdi-trending-up',
        permission: 'mrr-new:view'
      },
      {
        key: 'mrr-b2c-b2b2c',
        link: '/finances/b2c-report-v2',
        title: 'MRR B2C / B2B2C',
        icon: 'mdi-account-group-outline',
        permission: 'mrr-b2c-b2b2c:view'
      },
      {
        key: 'gross-margin',
        link: '/metricas/contract-profitability',
        title: 'Gross Margin',
        icon: 'mdi-cash-multiple',
        permission: 'gross-margin:view'
      },
      {
        key: 'cac-cltv',
        link: '/metricas/cac-cltv',
        title: 'B2B CLTV',
        icon: 'mdi-account-cash-outline',
        permission: 'cac-cltv:view'
      },
      {
        key: 'b2c-cltv',
        link: '/metricas/b2c-cltv',
        title: 'B2C CLTV',
        icon: 'mdi-account-heart-outline',
        permission: 'b2c-cltv:view'
      },
      {
        key: 'pet-protection-metrics',
        link: '/metricas/mascotas-protegidas',
        title: 'Mascotas protegidas',
        icon: 'mdi-paw',
        permission: 'pet-protection-metrics:view'
      },
      {
        key: 'customer-success',
        link: '/metricas/contract-success',
        title: 'Customer Success',
        icon: 'mdi-trophy-outline',
        permission: 'customer-success:view'
      },
      {
        key: 'refund-metrics',
        link: '/metricas/reembolsos',
        title: 'Reembolsos',
        icon: 'mdi-cash-refund',
        permission: 'refund-metrics:view'
      },
      {
        key: 'petmed-metrics',
        link: '/metricas/petmed',
        title: 'PetMed',
        icon: 'mdi-stethoscope',
        permission: 'petmed-metrics:view'
      },
      {
        key: 'sponsorship-metrics',
        link: '/sponsorships/metricas',
        title: 'Apadrinamiento',
        icon: 'mdi-chart-line',
        permission: 'sponsorship-metrics:view'
      },
      {
        key: 'ces-metrics',
        link: '/metricas/ces',
        title: 'CES',
        icon: 'mdi-emoticon-happy-outline',
        permission: 'ces-metrics:view'
      },
    ]
  },
  {
    title: 'General',
    icon: 'mdi-view-dashboard',
    open: true,
    subLinks: [
      {
        key: 'companies',
        link: '/business-relationships/companies',
        title: 'Empresas',
        icon: 'mdi-office-building',
        permission: 'companies:view'
      },
      {
        key: 'contracts',
        link: '/contratos',
        title: 'Contratos',
        icon: 'mdi-file-document-outline',
        permission: 'contracts:view'
      },
      {
        key: 'plans',
        link: '/business-relationships/plans',
        title: 'Planes',
        icon: 'mdi-account-multiple',
        permission: 'plans:view'
      },
      {
        key: 'users',
        link: '/users',
        title: 'Usuarios',
        icon: 'mdi-account-group',
        permission: 'users:view'
      },
      {
        key: 'co-tutors',
        link: '/vetops/co-tutores',
        title: 'Co-tutores',
        icon: 'mdi-account-multiple-plus',
        permission: 'co-tutors:view'
      },
      {
        key: 'pets',
        link: '/pets',
        title: 'Mascotas',
        icon: 'mdi-paw',
        permission: 'pets:view'
      },
      {
        key: 'memberships',
        link: '/memberships',
        title: 'Membresías',
        icon: 'mdi-badge-account-horizontal',
        permission: 'memberships:view'
      },
      {
        key: 'my-downloads',
        link: '/my-downloads',
        title: 'Mis Descargas',
        icon: 'mdi-tray-arrow-down',
        permission: 'my-downloads:view'
      },
      {
        key: 'arco-requests',
        link: '/opposition-requests',
        title: 'Solicitudes ARCO+',
        icon: 'mdi-shield-account-outline',
        permission: 'arco-requests:view'
      },
      {
        key: 'shelter-pets',
        link: '/sponsorships/shelter-pets',
        title: 'Apadrinamiento',
        icon: 'mdi-heart',
        permission: 'shelter-pets:view'
      },
      {
        key: 'discount-club',
        link: '/business-relationships/alliances',
        title: 'Club de Descuentos',
        icon: 'mdi-tag-multiple',
        permission: 'discount-club:view'
      },
      {
        key: 'poster-ads',
        link: '/business-relationships/poster-ads',
        title: 'Posters de beneficios',
        icon: 'mdi-view-carousel-outline',
        permission: 'poster-ads:view'
      }
    ]
  },
  {
    title: 'Revenue',
    icon: 'mdi-podium',
    subLinks: [
      {
        key: 'client-org-charts',
        link: '/clients/org-charts',
        title: 'Organigramas',
        icon: 'mdi-sitemap',
        permission: 'client-org-charts:view'
      },
      {
        key: 'b2b-renewals',
        link: '/metricas/renovaciones',
        title: 'B2B Renovaciones',
        icon: 'mdi-calendar-refresh',
        permission: 'b2b-renewals:view'
      },
      {
        key: 'b2c-renewals',
        link: '/vencimientos',
        title: 'B2C Renovaciones',
        icon: 'mdi-calendar-alert-outline',
        permission: 'b2c-renewals:view'
      },
      {
        key: 'plan-ranking',
        link: '/business-relationships/plans/ranking',
        title: 'Ranking de planes',
        icon: 'mdi-podium',
        permission: 'plan-ranking:view'
      },
      {
        key: 'contract-ranking',
        link: '/contratos/ranking',
        title: 'Ranking de Contratos',
        icon: 'mdi-file-chart',
        permission: 'contract-ranking:view'
      },
      {
        key: 'quote-builder',
        link: '/cotizador',
        title: 'Cotizador',
        icon: 'mdi-calculator-variant-outline',
        permission: 'quote-builder:view'
      },
      {
        key: 'quotes',
        link: '/cotizaciones',
        title: 'Cotizaciones',
        icon: 'mdi-file-document-multiple-outline',
        permission: ['quotes:view', 'quotes:view-own']
      },
    ]
  },
  {
    title: 'VetOps',
    icon: 'mdi-medical-bag',
    subLinks: [
      {
        key: 'refund-inbox',
        link: '/refunds/new',
        title: 'Inbox de Reembolsos',
        icon: 'mdi-inbox-arrow-down',
        permission: 'refund-inbox:view'
      },
      {
        key: 'register-refund',
        link: '/registers/register-refunds',
        title: 'Registrar Reembolso',
        icon: 'mdi-magnify',
        permission: 'register-refund:view'
      },
      {
        key: 'refund-clinics',
        link: '/refunds/clinics',
        title: 'Clínicas de reembolso',
        icon: 'mdi-hospital-building',
        permission: 'refund-clinics:view'
      },
      {
        key: 'refund-redirect-profiles',
        link: '/refunds/redirect-profiles',
        title: 'Redirección de reembolsos',
        icon: 'mdi-call-split',
        permission: 'refund-redirect-profiles:view'
      },
      {
        key: 'register-service',
        link: '/registers/servicesrecords',
        title: 'Registrar Servicio',
        icon: 'mdi-plus-circle-outline',
        permission: ['service-records:read', 'service-records:read-own']
      },
      {
        key: 'droopy-clinics',
        link: '/clinics/droopy',
        title: 'Droopy',
        icon: 'mdi-hospital-building',
        permission: 'droopy-clinics:view'
      }
    ]
  },
  {
    title: 'Operaciones',
    icon: 'mdi-cog-sync',
    subLinks: [
      {
        key: 'bulk-uploads',
        link: '/cargas-masivas',
        title: 'Cargas Masivas',
        icon: 'mdi-table-arrow-up',
        permission: 'bulk-uploads:view'
      },
      {
        key: 'contract-renewal',
        link: '/renovacion-contrato',
        title: 'Renovación de Contrato',
        icon: 'mdi-file-swap-outline',
        permission: 'contract-renewal:view'
      },
      {
        key: 'terms-notification',
        link: '/terms-notification',
        title: 'Términos y Condiciones',
        icon: 'mdi-file-document-edit-outline',
        permission: 'terms-notification:view'
      }
    ]
  },
  {
    title: 'Finanzas',
    icon: 'mdi-bank',
    subLinks: [
      {
        key: 'actuarial-analysis',
        link: '/finanzas/analisis-actuarial',
        title: 'Análisis actuarial',
        icon: 'mdi-chart-bell-curve-cumulative',
        permission: 'actuarial-analysis:view'
      },
      {
        key: 'mrr-billing-entry',
        link: '/finances/billing/individual',
        title: 'Registro de MRR',
        icon: 'mdi-file-plus-outline',
        permission: 'mrr-billing-entry:create'
      },
      {
        key: 'mrr-billing-import',
        link: '/finances/billing/importar',
        title: 'Importar MRR',
        icon: 'mdi-table-arrow-up',
        permission: 'mrr-billing-import:import'
      },
      {
        key: 'service-ranking',
        link: '/metricas/service-ranking',
        title: 'Ranking de servicios',
        icon: 'mdi-medical-bag',
        permission: 'service-ranking:view'
      },
      {
        key: 'clinic-billing',
        link: '/finanzas/facturacion-clinicas',
        title: 'Facturación de Clínicas',
        icon: 'mdi-receipt-text-outline',
        permission: 'clinic-billing:view'
      }
    ]
  },
  {
    title: 'Mantenedores',
    icon: 'mdi-cog',
    subLinks: [
      {
        key: 'products',
        link: '/contratos/productos',
        title: 'Productos',
        icon: 'mdi-package-variant-closed',
        permission: 'products:view'
      },
      {
        key: 'brokers',
        link: '/configuracion/brokers',
        title: 'Brokers',
        icon: 'mdi-handshake',
        permission: 'brokers:view'
      },
      {
        key: 'admins',
        link: '/configuracion/ejecutivos',
        title: 'Administradores',
        icon: 'mdi-shield-account',
        permission: 'admins:view'
      },
      {
        key: 'exchange-rate',
        link: '/finances/tipo-cambio',
        title: 'Tasa de Cambio',
        icon: 'mdi-currency-usd',
        permission: 'exchange-rate:view'
      },
      {
        key: 'refund-reply-templates',
        link: '/mantenedores/respuestas-tipo',
        title: 'Respuestas para reembolsos',
        icon: 'mdi-text-box-multiple-outline',
        permission: 'refund-reply-templates:view'
      },
      {
        key: 'services',
        link: '/mantenedores/servicios',
        title: 'Servicios',
        icon: 'mdi-cog-box',
        permission: 'services:view'
      },
      {
        key: 'business-lines',
        link: '/mantenedores/lineas-negocio',
        title: 'Líneas de negocio',
        icon: 'mdi-sitemap',
        permission: 'business-lines:view'
      }
    ]
  },
  {
    title: 'Herramientas Internas',
    icon: 'mdi-toolbox-outline',
    subLinks: [
      {
        key: 'ranger-test-chat',
        link: '/herramientas-internas/ranger-test-chat',
        title: 'Chat de prueba Ranger',
        icon: 'mdi-robot-outline',
        permission: 'ranger-test-chat:use'
      }
    ]
  }
];

/** Key of the section used when the admin has no (valid) preference: MRR Resumen. */
export const DEFAULT_SECTION_KEY = 'mrr-resume';

/** Route shown when the admin may not open a section, or has none at all (spec 131). */
export const NO_ACCESS_PATH = '/sin-acceso';

const allowAll = () => true;

/**
 * Whether the logged-in admin may see a menu leaf (spec 131). A leaf with
 * `permission` needs that tag (or any of them, for a list); a leaf without it
 * is Super admin only (`*`). Requires `loadPermissions()` to have resolved.
 * @param {{ permission?: string|string[] }} leaf
 * @returns {boolean}
 */
export function isSectionAllowed(leaf) {
  return isPermissionDeclared(leaf.permission) ? can(leaf.permission) : isSuperAdmin();
}

/**
 * Menu groups with only the leaves that pass `isAllowed`; empty groups are dropped.
 * @param {(leaf: object) => boolean} [isAllowed]
 * @returns {object[]}
 */
export function getAllowedNavGroups(isAllowed = isSectionAllowed) {
  return NAV_GROUPS.map((group) => ({
    ...group,
    subLinks: (group.subLinks || []).filter(isAllowed)
  })).filter((group) => group.subLinks.length > 0);
}

/**
 * Flat list of the sections an admin can pick as their landing.
 * @param {(leaf: object) => boolean} [isAllowed] - leaf filter; by default every leaf
 *   (pass `isSectionAllowed` to get only the logged-in admin's sections).
 * @returns {{ key: string, link: string, title: string, icon: string, group: string }[]}
 */
export function getSelectableSections(isAllowed = allowAll) {
  return NAV_GROUPS.flatMap((group) =>
    (group.subLinks || []).filter(isAllowed).map(({ key, link, title, icon }) => ({
      key,
      link,
      title,
      icon,
      group: group.title,
    }))
  );
}

/**
 * Finds a selectable section by its key.
 * @param {string|null|undefined} key
 * @param {(leaf: object) => boolean} [isAllowed] - same filter as `getSelectableSections`.
 * @returns {{ key: string, link: string, title: string, icon: string, group: string }|null}
 */
export function findSection(key, isAllowed = allowAll) {
  if (!key) return null;
  return getSelectableSections(isAllowed).find((section) => section.key === key) || null;
}

/** Route of the default section (MRR Resumen, `/finances/metas`). */
export const DEFAULT_LANDING = findSection(DEFAULT_SECTION_KEY).link;

/**
 * Route the admin lands on when entering the panel (login or `/`).
 * Fallback chain: the saved section if allowed, else MRR Resumen if allowed,
 * else the first allowed section, else `/sin-acceso`. Never an error.
 * @param {string|null|undefined} key - `preferences.defaultSection` from `/me`.
 * @param {(leaf: object) => boolean} [isAllowed] - by default the logged-in admin's tags.
 * @returns {string}
 */
export function resolveLanding(key, isAllowed = isSectionAllowed) {
  const section =
    findSection(key, isAllowed) ||
    findSection(DEFAULT_SECTION_KEY, isAllowed) ||
    getSelectableSections(isAllowed)[0];
  return section ? section.link : NO_ACCESS_PATH;
}
